/**
 * Reading the viewer's IP off an incoming server request, and forwarding it
 * on the backend calls we make on their behalf during SSR.
 *
 * Server-side fetches (page `getInitialProps`) leave Render from a small set
 * of egress IPs, so without this the backend sees every visitor as the same
 * handful of addresses and cannot attribute or throttle traffic per visitor.
 *
 * The trust contract, which the backend has to hold up its end of:
 *   - The backend must only read `X-Forwarded-For` on requests arriving from
 *     the SSR egress IPs. Anyone can send the header directly.
 *   - The leftmost entry is the viewer. We only ever put a CloudFront-attested
 *     address there (see `viewerIpHeaders`), so it is not viewer-controlled.
 *   - Entries to the right are the hops in between, ending with the SSR
 *     server's own egress IP once the backend's ingress appends it. The SSR
 *     hop stays visible in the chain; nothing is erased.
 */
import type {
  IncomingHttpHeaders,
  IncomingMessage,
} from 'http';

// A viewer can send an arbitrarily long X-Forwarded-For, and we pass the chain
// through: cap it so they cannot inflate the header we send to the backend.
const MAX_FORWARDED_ENTRIES = 20;

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const single = (value: string | Array<string> | undefined): string | undefined => (
  Array.isArray(value) ? value[0] : value
);

const isIp = (value: string): boolean => {
  if (IPV4.test(value)) {
    return true;
  }

  try {
    // The URL parser validates IPv6 literals and throws on anything else.
    // `net.isIP` would be tidier, but this module reaches the client bundle
    // (requestHelpers -> fetchAdvocatesCount) where `net` does not resolve.
    return !!new URL(`http://[${value}]`);
  } catch {
    return false;
  }
};

/**
 * CloudFront rewrites `CloudFront-Viewer-Address` on every request, so it is
 * the one viewer address we can trust. Note that CloudFront *appends* to
 * `X-Forwarded-For` rather than replacing it, so the leftmost entry there is
 * whatever the viewer sent — never treat it as attested.
 */
const cloudFrontViewerIp = (headers: IncomingHttpHeaders): string | undefined => {
  // CloudFront-Viewer-Address is "ip:port" (also for IPv6): strip the port.
  const ip = single(headers['cloudfront-viewer-address'])?.replace(/:\d+$/, '');

  return ip && isIp(ip) ? ip : undefined;
};

/**
 * Best-effort viewer IP, for attribution that tolerates a wrong answer (the
 * PostHog `$ip` on beacon events). The `x-forwarded-for` fallback is viewer-
 * controlled, so do not use this for anything a viewer could gain by forging
 * — `viewerIpHeaders` deliberately does not.
 */
export const viewerIp = (headers: IncomingHttpHeaders): string | undefined => {
  const attested = cloudFrontViewerIp(headers);

  if (attested) {
    return attested;
  }

  const claimed = single(headers['x-forwarded-for'])?.split(',')[0].trim();

  return claimed && isIp(claimed) ? claimed : undefined;
};

/**
 * Headers to attach to a backend call made on the viewer's behalf.
 *
 * Undefined — sending no `X-Forwarded-For` at all, so the backend falls back
 * to our egress IP — when there is no incoming request (`getInitialProps`
 * running in the browser, where the request already carries the viewer's own
 * IP) or no CloudFront-attested address (a request that bypassed CloudFront,
 * where the only candidate is viewer-controlled and forwarding it under a
 * header the backend trusts would let a viewer forge their identity).
 */
export const viewerIpHeaders = (
  req?: IncomingMessage
): { 'X-Forwarded-For': string } | undefined => {
  const ip = req && cloudFrontViewerIp(req.headers);

  if (!ip) {
    return undefined;
  }

  // Pass the received chain through so the intervening hops survive, with the
  // attested address leftmost. CloudFront has usually already put it there;
  // prepend it when a viewer-supplied prefix pushed it along.
  const inbound = (single(req.headers['x-forwarded-for']) || '')
    .split(',')
    .map(entry => entry.trim())
    .filter(isIp);
  const chain = inbound[0] === ip ?
    inbound :
    [
      ip,
      ...inbound,
    ];

  return { 'X-Forwarded-For': chain.slice(0, MAX_FORWARDED_ENTRIES).join(', ') };
};
